import { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { BASE, SURFACE, LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, DISPLAY, BODY, MONO } from '../lib/constants';
import { Mo, Bd, Dp, Ic } from './shared';

const CARD_STYLES = {
  debtKilled: { accent: GOLD, icon: 'skull', bg: `linear-gradient(165deg, #1a1918 0%, #1f1a14 40%, #1a1410 100%)` },
  debtFree: { accent: GOLD, icon: 'trophy', bg: `linear-gradient(165deg, #1a1918 0%, #1e1a10 40%, #1a1610 100%)` },
  streak: { accent: GOLD, icon: 'flame', bg: `linear-gradient(165deg, #1a1918 0%, #1a1614 40%, #1a1410 100%)` },
  yearReview: { accent: GOLD, icon: 'chart', bg: `linear-gradient(165deg, #1a1918 0%, #1a1816 40%, #181614 100%)` },
};

export function ShareCard({ type = 'debtKilled', stats, onClose }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const style = CARD_STYLES[type] || CARD_STYLES.debtKilled;

  const doDownload = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, { quality: 0.95, pixelRatio: 2, backgroundColor: BASE });
      const link = document.createElement('a');
      link.download = `dettio-${type}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('Download failed:', e); }
    setDownloading(false);
  }, [type]);

  const doShare = useCallback(async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { quality: 0.95, pixelRatio: 2, backgroundColor: BASE });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `dettio-${type}.png`, { type: 'image/png' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Dettio Progress', text: stats.shareText || 'Check out my financial freedom progress!' });
      } else {
        await navigator.clipboard?.write?.([new ClipboardItem({ 'image/png': blob })]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      const text = stats.shareText || `I just ${type === 'debtKilled' ? 'killed a debt' : type === 'debtFree' ? 'became DEBT FREE' : 'hit a new streak'} with Dettio!`;
      await navigator.clipboard?.writeText?.(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [type, stats]);

  return (
    <div data-testid="share-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(8px)' }}>
      <button className="safe-top-abs" onClick={onClose} data-testid="share-close-btn" style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 8, zIndex: 210 }}>{Ic.x(24, MUTE)}</button>

      {/* The Card */}
      <div ref={cardRef} data-testid="share-card" style={{ width: 340, padding: 0, borderRadius: 8, overflow: 'hidden' }}>
        {/* Top gradient bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${EMBER}, ${GOLD}, ${EMBER})` }} />
        <div style={{ background: style.bg, padding: '32px 28px 28px', position: 'relative' }}>
          {/* Subtle glow */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180, background: `radial-gradient(ellipse at 50% 0%, rgba(232,168,56,0.08) 0%, transparent 70%)`, pointerEvents: 'none' }} />

          {/* Branding */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, position: 'relative' }}>
            {Ic.flame(14, GOLD)}
            <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--mute)', letterSpacing: 2 }}>DETTIO</Mo>
          </div>

          {/* Achievement */}
          {type === 'debtKilled' && (
            <div style={{ position: 'relative' }}>
              <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--ember)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>DEBT KILLED</Mo>
              <Dp style={{ fontSize: 28, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1, marginBottom: 4 }}>{stats.debtName}</Dp>
              <Dp style={{ fontSize: 36, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2, marginBottom: 20 }}>${stats.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Dp>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ l: 'TIME', v: stats.time || '7 mo' }, { l: 'INTEREST SAVED', v: `$${stats.interestSaved || 312}` }, { l: 'PAYMENTS', v: stats.payments || '7' }].map(s => (
                  <div key={s.l} style={{ flex: 1, padding: '10px 8px', background: 'rgba(240,236,228,0.04)', textAlign: 'center' }}>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>{s.l}</Mo>
                    <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)' }}>{s.v}</Mo>
                  </div>
                ))}
              </div>
            </div>
          )}

          {type === 'debtFree' && (
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{ marginBottom: 8 }}>{Ic.trophy(40, GOLD)}</div>
              <Dp style={{ fontSize: 34, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2, marginBottom: 4 }}>DEBT FREE</Dp>
              <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 20 }}>Every single dollar — conquered.</Bd>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[{ l: 'TOTAL KILLED', v: stats.totalKilled || '$21,697' }, { l: 'INTEREST SAVED', v: stats.interestSaved || '$4,218' }, { l: 'TIME', v: stats.time || '14 months' }, { l: 'BADGES', v: stats.badges || '7 of 7' }].map(s => (
                  <div key={s.l} style={{ padding: '10px 8px', background: 'rgba(240,236,228,0.04)', textAlign: 'center' }}>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>{s.l}</Mo>
                    <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>{s.v}</Mo>
                  </div>
                ))}
              </div>
            </div>
          )}

          {type === 'streak' && (
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{ marginBottom: 8 }}>{Ic.flame(40, GOLD)}</div>
              <Mo style={{ fontSize: 48, fontWeight: 700, color: 'var(--gold)', display: 'block', lineHeight: 1 }}>{stats.streak || 3}</Mo>
              <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginTop: 4, marginBottom: 20 }}>MONTH STREAK</Mo>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ l: 'PAID THIS STREAK', v: stats.paidThisStreak || '$1,840' }, { l: 'DEBTS ACTIVE', v: stats.debtsActive || '5' }, { l: 'PROGRESS', v: stats.progress || '13%' }].map(s => (
                  <div key={s.l} style={{ flex: 1, padding: '10px 8px', background: 'rgba(240,236,228,0.04)', textAlign: 'center' }}>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>{s.l}</Mo>
                    <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)' }}>{s.v}</Mo>
                  </div>
                ))}
              </div>
            </div>
          )}

          {type === 'yearReview' && (
            <div style={{ position: 'relative' }}>
              <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>YEAR IN REVIEW</Mo>
              <Dp style={{ fontSize: 28, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1, marginBottom: 20 }}>{stats.year || '2025'}</Dp>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[{ l: 'TOTAL PAID', v: stats.totalPaid || '$6,840' }, { l: 'DEBTS KILLED', v: stats.debtsKilled || '1' }, { l: 'INTEREST SAVED', v: stats.interestSaved || '$1,847' }, { l: 'BEST MONTH', v: stats.bestMonth || 'April' }].map(s => (
                  <div key={s.l} style={{ padding: '10px 8px', background: 'rgba(240,236,228,0.04)', textAlign: 'center' }}>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>{s.l}</Mo>
                    <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>{s.v}</Mo>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer line */}
          <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${FAINT}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1 }}>dettio.app</Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1 }}>Taking back what's mine</Mo>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, width: 340 }}>
        <button onClick={doDownload} disabled={downloading} data-testid="share-download-btn" style={{ flex: 1, padding: '14px 0', background: GOLD, border: 'none', color: 'var(--base)', fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {Ic.download(14, BASE)} {downloading ? 'SAVING...' : 'SAVE IMAGE'}
        </button>
        <button onClick={doShare} data-testid="share-share-btn" style={{ flex: 1, padding: '14px 0', background: 'transparent', border: `1px solid ${GHOST}`, color: 'var(--light)', fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {Ic.send(14, LIGHT)} {copied ? 'COPIED!' : 'SHARE'}
        </button>
      </div>
    </div>
  );
}

export function ShareButton({ onClick, label = 'SHARE YOUR WIN' }) {
  return (
    <button onClick={onClick} data-testid="share-win-btn" style={{ width: '100%', padding: '14px 0', marginTop: 16, background: 'transparent', border: `1px solid ${GOLD}30`, color: 'var(--gold)', fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      {Ic.send(14, GOLD)} {label}
    </button>
  );
}
